import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { APP_CONSTANTS } from '../../app.constants';

import { LibraryMarketSettingsDto } from '../../../../server/types/libraryMarket';
import { LibrarySettings } from '../../../../server/types/library-settings';
import { AvailableCountry } from '@server/types/countries';
import { Units } from '@turf/helpers';

// At the top of the file or in a shared constants file
const DEMO_LIBRARY_ID = 666;
const YOUR_INSTITUTION_LIBRARY_ID = 664;
const ANYTOWN_LIBRARY_ID = 665;

// distance units - not being stored in the database, no reason to expose them as a setting
const DISTANCE_UNITS_BY_COUNTRY = {
    AUS: 'kilometres', // British Spelling
    CAN: 'kilometres', // British Spelling
    NZL: 'kilometres', // British Spelling
    USA: 'miles'
} satisfies Record<AvailableCountry, Units>;

// distance units - not being stored in the database, no reason to expose them as a setting
const DISTANCE_UNITS_BY_COUNTRY_TRANSLATION = {
    AUS: 'common.units.kilometres', // British Spelling
    CAN: 'common.units.kilometres', // British Spelling
    NZL: 'common.units.kilometres', // British Spelling
    USA: 'common.units.miles'
} satisfies Record<AvailableCountry, string>;

export function getDistanceUnitTranslation(country: AvailableCountry): string | undefined {
    return DISTANCE_UNITS_BY_COUNTRY_TRANSLATION[country];
}

/**
 * Checks if a library ID corresponds to a sandbox/test library.
 * Demo libraries include Demo Library (666), and Your Institution (664).
 *
 * @param id - The library ID to check
 * @returns boolean - True if the library is a sandbox library, false otherwise
 */
export const isDemoLibrary = (id: number): boolean => {
    return [DEMO_LIBRARY_ID, YOUR_INSTITUTION_LIBRARY_ID].includes(id);
};

interface LibraryConfiguration {
    smartycat: {
        libraryCode: string;
    };
    libraryMarket: LibraryMarketSettingsDto | null;
}

interface CensusLayer {
    layer: string;
    source: string;
}

interface MapValues {
    censusLayer: CensusLayer;
    coordinates: number[];
}

export interface AdminBranch {
    id: number;
    libraryid: number;
    name: string;
    consortiumId?: number;
    branchCode: string;
    active: number;
    populationServed?: any;
    venueID?: any;
    coordinates?: string;
    patronsInServiceArea?: number;
}

export interface Library {
    id: number;
    name: string;
    settings: LibrarySettings;
    fiscalYear?: string;
    coordinates?: string;
    populationServed?: number;
    patronsInServiceArea?: number;
    accountId: number;
    consortiumid?: any;
    lastUpdate: Date;
    mapValues: MapValues;
    active: number;
}

export interface LibraryBranch {
    Name: string;
    ItemBranchAbbr: string;
    BranchId: number;
    populationServed: number;
    venueID: string;
    coordinates: string;
}

export interface LibraryUser {
    id: number;
    userName: string;
    email: string;
}

export interface LibraryZone {
    id: string;
    name: string;
    branches: number[];
}

// this model is for use in the UI outside of the admin settings.
// these two models are inconsistent, with some values being calculated
// or pulled from nested structured data (like map values and coordinates).
// we need to audit these fields and determine which to keep.
// ideally, we could use the same model
export interface LibraryFull {
    id: number;
    accountId: number;
    name: string;
    cid?: number;
    cname?: string | null;
    groupId?: number;
    groupName?: string | null;
    settings: LibrarySettings;
    fiscalYear: string;
    coordinates?: string;
    censusLayer?: string;
    populationServed: number;
    cPopulationServed?: number;
    cCoordinates?: string;
    lgPopulationServed?: number;
    lgCoordinates?: string;
    lgBranchId?: number;
    patronsInServiceArea?: number;
}

interface LibraryGridSettings {
    holdsEnabled?: boolean;
    orderedItemsEnabled?: boolean;
    fulfilledHoldsEnabled?: boolean;
    requestedHoldsEnabled?: boolean;
    lastUpdatedDateEnabled?: boolean;
    lastInventoryDateEnabled?: boolean;
    lastStatusUpdateEnabled?: boolean;
}

/** San Mateo, the only library with FindIt classification data. */
const FINDIT_LIBRARY_ID = 92;

@Injectable({ providedIn: 'root' })
export class LibraryService {
    // Shared API URL prefix sourced from APP_CONSTANTS so the "/api" mount
    // point is not hard-coded throughout this service.
    private readonly API_PREFIX = APP_CONSTANTS.API_PREFIX;
    private readonly library$ = new BehaviorSubject<LibraryFull | null>(null);

    /** hasLccRecords answers, by library ID. */
    private readonly lccRecordChecks = new Map<number, Observable<boolean>>();
    private readonly branches$ = new BehaviorSubject<LibraryBranch[]>([]);
    private readonly currentBranch$ = new BehaviorSubject<LibraryBranch | null>(null);

    private gridSettings$ = new BehaviorSubject<LibraryGridSettings | null>(null);
    private libraryConfiguration$ = new BehaviorSubject<LibraryConfiguration | null>(null);

    constructor(private http: HttpClient) {
        this.library$
            .pipe(
                takeUntilDestroyed(),
                filter((library) => !!library),
                map(({ id }) => id),
                distinctUntilChanged()
            )
            .subscribe((id) => {
                // any time the library changes, we can fetch extra data
                // such as the grid settings specific to the new library
                void this.fetchGridSettings(id);
                void this.fetchLibraryConfiguration(id);
            });
    }

    /**
     * Mainly to be used by admin pages that perform library updates. This will
     * return the model used when saving, just update the library and save the changes.
     * @param id number
     * @returns
     */
    fetchLibrary(id: number): Observable<{ library: Library; branches: AdminBranch[] }> {
        return this.http
            .get<{ library: Library; branches: AdminBranch[] }>(`${this.API_PREFIX}/libraries/getLibrary`, { params: { id } })
            .pipe(catchError((err) => this.handleError(err)));
    }

    /**
     * Gets an observable of the current branch.
     *
     * @returns Observable<LibraryBranch | null> - Stream that emits the current branch, or null if none selected
     */
    getCurrentBranch(): Observable<LibraryBranch | null> {
        return this.currentBranch$.asObservable();
    }

    /**
     * Gets an observable of the current branch name.
     * Convenience method that extracts just the name from the branch object.
     *
     * @returns Observable<string | null> - Stream that emits the branch name, or null if no branch selected
     */
    getCurrentBranchName(): Observable<string | null> {
        return this.currentBranch$.asObservable().pipe(map((branch) => (branch !== null ? branch.Name : null)));
    }

    /**
     * Gets an observable of all available branches.
     * When a library ID is provided, branches are fetched for that specific library.
     * Otherwise, the current in-memory branch stream is returned.
     *
     * @param libraryId - Optional library ID to fetch branches for
     * @returns Observable<LibraryBranch[]> - Stream that emits available branches
     */
    getCurrentBranches(libraryId?: number): Observable<LibraryBranch[]> {
        if (libraryId == null) {
            return this.branches$.asObservable();
        }

        return this.http
            .get<AdminBranch[]>(`${this.API_PREFIX}/libraries/getLibraryBranches`, { params: { library: libraryId } })
            .pipe(
                map((branches) =>
                    branches.map((branch) => ({
                        Name: branch.name,
                        ItemBranchAbbr: branch.branchCode,
                        BranchId: branch.id,
                        populationServed: Number(branch.populationServed ?? 0),
                        venueID: branch.venueID ? String(branch.venueID) : '',
                        coordinates: branch.coordinates ? String(branch.coordinates) : ''
                    }))
                ),
                catchError((err) => this.handleError(err))
            );
    }

    /**
     * Gets an observable of the current library.
     *
     * @returns Observable<LibraryFull | null> - Stream that emits the current library or null before selection
     */
    getCurrentLibrary(): Observable<LibraryFull | null> {
        return this.library$.asObservable();
    }

    /**
     * Whether the library has FindIt classification data, which is what makes the FindIt report
     * available: it decides both the "Physical Collection by FindIt" menu item and the FindIt tab of
     * Physical Collection Performance. Only San Mateo has FindIt data.
     */
    hasFindItData(libraryId: number): boolean {
        return libraryId === FINDIT_LIBRARY_ID;
    }

    /**
     * Whether the library has LCC classification records, which is what makes the LCC report
     * available: it decides both the "Physical Collection by LCC" menu item and the LCC tab of
     * Physical Collection Performance.
     *
     * Each library is checked once and its answer shared, since the menu and every page visit ask.
     * A failed check errors rather than answering "no", so callers can tell the two apart, and is
     * not kept, so the next call asks again.
     */
    hasLccRecords(libraryId: number): Observable<boolean> {
        let check = this.lccRecordChecks.get(libraryId);
        if (!check) {
            check = this.http
                .get<{ success: boolean }>(`${this.API_PREFIX}/report/hasLCCRecords`, {
                    params: { library: libraryId.toString() }
                })
                .pipe(
                    map(response => response.success),
                    catchError(error => {
                        this.lccRecordChecks.delete(libraryId);
                        return throwError(() => error);
                    }),
                    shareReplay(1)
                );
            this.lccRecordChecks.set(libraryId, check);
        }
        return check;
    }

    /**
     * Gets the current library and branch as a combined observable.
     * Only emits when both library and branch are non-null, ensuring consumers
     * always receive valid data. Updates whenever either the library or branch changes.
     *
     * @returns Observable<{library: LibraryFull; branch: LibraryBranch}> - An observable that emits
     * an object containing both the current library and branch
     *
     * @example
     * this.libraryService.getCurrentLibraryAndBranch()
     *   .pipe(takeUntil(this.destroy$))
     *   .subscribe(({ library, branch }) => {
     *     console.log(`Library: ${library.name}, Branch: ${branch.Name}`);
     *   });
     */
    getCurrentLibraryAndBranch(): Observable<{ library: LibraryFull; branch: LibraryBranch }> {
        return combineLatest([this.library$, this.currentBranch$]).pipe(
            filter(([library, branch]) => library !== null && branch !== null),
            map(([library, branch]) => {
                return {
                    library,
                    branch
                };
            })
        );
    }

    /**
     * Gets the library settings with normalized defaults.
     * Returns settings for the current library with fallback values for any missing fields.
     * Should keep this structure flat so that changes can be handled easily.
     *
     * @returns LibrarySettings - The normalized library settings object
     */
    getLibrarySettings(): LibrarySettings {
        const settings = this.library$.value?.settings;
        const countryCode = settings?.countryCode ?? 'USA';

        // ditance units - not being stored in the database, no reason to expose them as a setting
        const distanceUnits = DISTANCE_UNITS_BY_COUNTRY[countryCode] ?? 'miles';

        // this is where we normalize the settings. if data types or fields
        // have moved/changed, provide fallbacks and deprecated checks here
        // `settingField = settings.newField || settings.oldField`
        return {
            countryCode,
            distanceUnits,
            catalogUrl: settings?.catalogUrl ?? '',
            theme: settings?.theme ?? 'default',
            customColors: settings?.customColors ?? [],
            doaAgeMin: settings?.doaAgeMin ?? 3,
            doaAgeMax: settings?.doaAgeMax ?? 15
        };
    }

    /**
     * Gets an observable of the current library's configuration.
     * Configuration includes integration settings and feature-specific options.
     *
     * @returns Observable<LibraryConfiguration | null> - Stream of library configuration, or null if not loaded
     */
    getLibraryConfiguration(): Observable<LibraryConfiguration | null> {
        return this.libraryConfiguration$.asObservable();
    }

    /**
     * Retrieves the list of patron types configured for a library.
     * Patron types are used to categorize library users (e.g., 'Adult', 'Child', 'Senior').
     *
     * @param id - The library ID
     * @returns Observable<string[]> - Array of patron type names
     */
    getLibraryPatronTypes(id: number): Observable<string[]> {
        return this.http
            .get<{ data: string[] }>(`${this.API_PREFIX}/libraries/getPatronTypes`, {
                params: { libraryId: id }
            })
            .pipe(
                map((res: { data: string[] }) => res.data),
                catchError((err) => this.handleError(err))
            );
    }

    /**
     * Retrieves all users associated with a specific library.
     *
     * @param id - The library ID
     * @returns Observable<LibraryUser[]> - Array of users with access to the library
     */
    getLibraryUsers(id: number): Observable<LibraryUser[]> {
        return this.http
            .get<{ data: LibraryUser[] }>(`${this.API_PREFIX}/libraries/getLibraryUsers`, {
                params: { libraryId: id }
            })
            .pipe(
                map((res: { data: LibraryUser[] }) => res.data),
                catchError((err) => this.handleError(err))
            );
    }

    /**
     * Retrieves all zones configured for a library.
     * Zones are groupings of branches used for reporting and analytics.
     *
     * @param libraryId - The library ID
     * @returns Observable<LibraryZone[]> - Array of zones with their associated branches
     */
    getLibraryZones(libraryId: number): Observable<LibraryZone[]> {
        return this.http
            .get<{ data: any[] }>(`${this.API_PREFIX}/libraries/getLibraryZones`, {
                params: { libraryId }
            })
            .pipe(
                map((res: { data: any[] }) => res.data),
                map((data) => {
                    const zones = {
                        all: {
                            id: 'ALL',
                            name: 'All',
                            branches: []
                        }
                    };
                    data.forEach((item) => {
                        if (!zones[item.zoneId]) {
                            zones[item.zoneId] = {
                                id: item.zoneId,
                                name: item.name,
                                branches: []
                            };
                        }
                        zones[item.zoneId].branches.push(item.branchId);
                    });
                    return Object.values(zones);
                }),
                catchError((err) => this.handleError(err))
            );
    }

    /**
     * Gets an observable of the current library's grid settings.
     * Grid settings control which columns are enabled in various data grids.
     *
     * @returns Observable<LibraryGridSettings | null> - Stream of grid settings, or null if not loaded
     */
    getGridSettings(): Observable<LibraryGridSettings | null> {
        return this.gridSettings$.asObservable();
    }

    /**
     * Checks if the current library is the demo library (ID: 666).
     * Returns true by default when no library is selected.
     *
     * @returns Observable<boolean> - Stream that emits true if demo library or null, false otherwise
     */
    isDemo(): Observable<boolean> {
        return this.isLibraryId(DEMO_LIBRARY_ID);
    }

    /**
     * Checks if the current library is Your Institution (ID: 664).
     * Returns true by default when no library is selected.
     *
     * @returns Observable<boolean> - Stream that emits true if Your Institution or null, false otherwise
     */
    isYourInstitution(): Observable<boolean> {
        return this.isLibraryId(YOUR_INSTITUTION_LIBRARY_ID);
    }

    /**
     * Checks if the current library is AnyTown (ID: 665).
     * Returns true by default when no library is selected.
     *
     * @returns Observable<boolean> - Stream that emits true if AnyTown or null, false otherwise
     */
    isAnyTown(): Observable<boolean> {
        return this.isLibraryId(ANYTOWN_LIBRARY_ID);
    }

    /**
     * Checks if the current library is a sandbox/test library.
     * Sandbox libraries include Your Institution (ID: 664) and AnyTown (ID: 665).
     * Returns true by default when no library is selected.
     *
     * @returns Observable<boolean> - Stream that emits true if sandbox library or null, false otherwise
     */
    isSandbox(): Observable<boolean> {
        return combineLatest({
            anyTown: this.isAnyTown(),
            yourInstitution: this.isYourInstitution()
        }).pipe(map((result) => result.anyTown || result.yourInstitution));
    }

    /**
     * Reloads the grid settings and library configuration for the currently selected library.
     * Useful when settings need to be refreshed without changing the library selection.
     */
    reloadSettingsAndConfigs(): void {
        const library = this.library$.value;
        if (library) {
            this.fetchGridSettings(library.id);
            this.fetchLibraryConfiguration(library.id);
        }
    }

    /**
     * Sets the current branch for the selected library.
     *
     * @param branch - The branch to set as current
     */
    setCurrentBranch(branch: LibraryBranch): void {
        this.currentBranch$.next(branch);
    }

    /**
     * Sets the list of available branches for the current library.
     *
     * @param branches - Array of branches to make available for selection
     */
    setCurrentBranches(branches: LibraryBranch[]): void {
        this.branches$.next(branches);
    }

    /**
     * Sets the current library and triggers all related side effects.
     * Automatically fetches grid settings and library configuration for the new library.
     *
     * @param library - The library to set as current
     */
    setCurrentLibrary(library: LibraryFull): void {
        const previousLibraryId = this.library$.value?.id;
        this.library$.next(library);
        if (previousLibraryId !== library.id) {
            this.currentBranch$.next(null);
            this.branches$.next([]);
        }
    }

    /**
     * Updates a branch's information in the database.
     *
     * @param branch - The branch object with updated information
     * @param libraryId - The ID of the library the branch belongs to
     * @returns Observable<{success: boolean}> - Observable indicating whether the update was successful
     */
    updateBranch(branch: AdminBranch, libraryId: number): Observable<{ success: boolean }> {
        const body = {
            branch,
            libraryId
        };

        return this.http
            .post<{ success: boolean }>(`${this.API_PREFIX}/libraries/updateBranch`, body)
            .pipe(catchError((err) => this.handleError(err)));
    }

    /**
     * Updates a library's information in the database.
     *
     * @param library - The library object with updated information
     * @returns Observable<{success: boolean}> - Observable indicating whether the update was successful
     */
    updateLibrary(library: Library): Observable<{ success: boolean }> {
        return this.http
            .post<{ success: boolean }>(`${this.API_PREFIX}/libraries/updateLibrary`, library)
            .pipe(catchError((err) => this.handleError(err)));
    }

    /**
     * Fetches and caches grid settings for a specific library.
     * Called automatically when the library changes.
     *
     * @param libraryId - The library ID to fetch settings for
     */
    private async fetchGridSettings(libraryId: number): Promise<void> {
        try {
            const { data } = await firstValueFrom(
                this.http.get<{ data: any }>(`${this.API_PREFIX}/libraries/getGridSettings`, {
                    params: { libraryId }
                })
            );
            if (this.library$.value?.id !== libraryId) {
                return;
            }
            this.gridSettings$.next(data);
        } catch (e) {
            if (this.library$.value?.id === libraryId) {
                this.gridSettings$.next(null);
            }
            console.error('unable to fetch library grid settings', e);
        }
    }

    /**
     * Fetches and caches library configuration for a specific library.
     * Called automatically when the library changes.
     *
     * @param libraryId - The library ID to fetch configuration for
     */
    private async fetchLibraryConfiguration(libraryId: number): Promise<void> {
        try {
            const { data } = await firstValueFrom(
                this.http.get<{ data: any }>(`${this.API_PREFIX}/libraries/libraryConfiguration`, {
                    params: { libraryId }
                })
            );

            if (this.library$.value?.id !== libraryId) {
                return;
            }
            this.libraryConfiguration$.next(data);
        } catch (e) {
            if (this.library$.value?.id === libraryId) {
                this.libraryConfiguration$.next(null);
            }
            console.error('unable to fetch library configurations', e);
        }
    }

    /**
     * Handles HTTP errors and logs them.
     *
     * @param error - The error response from an HTTP request
     * @returns Observable<never> - An observable that throws the error
     */
    private handleError(error: Response | any): Observable<never> {
        console.error('ApiService::handleError', error);
        return throwError(() => error);
    }

    /**
     * Helper method to check if the current library matches a specific library ID.
     * Returns true by default when no library is selected.
     *
     * @param targetId - The library ID to check against
     * @returns Observable<boolean> - Stream that emits true if library matches targetId or is null, false otherwise
     */
    private isLibraryId(targetId: number): Observable<boolean> {
        return this.library$.pipe(map((library) => (library ? library.id === targetId : true)));
    }
}
